// Styled replacement for the native confirm() behind `turbo_confirm`, assigned
// to `Turbo.config.forms.confirm` by entrypoints/application.js. Turbo calls it
// as `confirm(message, formElement, submitter)`; for a `data-turbo-method` link
// the form is the one Turbo generates (its `method` attribute is that verb) and
// the submitter is null.
//
// Look (CONEK-8763): the markup and classes of conek_ui's Ui::AlertModalComponent
// / Ui::DeleteModalComponent — centred icon, title and message, an X in the
// corner, Ui::ButtonComponent buttons. The dialog itself lives here because
// Turbo's confirm has to be built client-side.
//
// Variant, first match wins:
//   1. `data-turbo-confirm-variant="danger|warning"` on the form or the submitter
//      (not on a `data-turbo-method` link: Turbo's generated form only copies
//      `data-turbo-confirm`, so a link relies on rule 3);
//   2. a "warning::" message prefix (stripped) → warning;
//   3. destructive → danger (trash icon, red confirm): the submission is a DELETE
//      (form `method="delete"`, or a hidden `_method=delete`), or the submitter
//      or anything inside it carries an unprefixed `bg-danger[-<shade>]` or
//      `text-danger[-<shade>]` class (a danger Ui::ButtonComponent, or a red
//      hand-styled trigger). Variant-prefixed tokens (`hover:text-danger-500`,
//      `dark:…`, `disabled:…`) do not count: the trigger is neutral at rest;
//   4. otherwise warning (triangle icon, primary confirm).
// Confirm label, first match wins: `data-turbo-confirm-confirm` on the element
// or submitter → the submitter's visible text (an input's value) → the meta
// default → "Confirm".
// Title/cancel: `data-turbo-confirm-title` / `data-turbo-confirm-cancel` on the
// element (CONEK-8204) → meta default → English.
// Defaults: every layout that loads the application entrypoint renders
// `<meta name="turbo-confirm-title|cancel|confirm|close">` in the interface
// language (CONEK-8233). They are read on every call, not cached, because Turbo
// merges the <head> on navigation and the language can change between visits.
// The English literals only remain as the last fallback for a page without them.

const FALLBACK_LABELS = {
  title: "Confirm action",
  cancel: "Cancel",
  confirm: "Confirm",
  close: "Close",
} as const

type LabelKey = keyof typeof FALLBACK_LABELS
type Variant = "danger" | "warning"

// An empty attribute or meta counts as unset, as it always has for the
// per-element overrides — hence truthiness checks rather than `??`.
function resolveLabel(override: string | undefined, key: LabelKey): string {
  if (override) return override
  const meta = document.head.querySelector<HTMLMetaElement>(
    `meta[name="turbo-confirm-${key}"]`,
  )
  if (meta?.content) return meta.content
  return FALLBACK_LABELS[key]
}

// Unprefixed tokens only: `hover:text-danger-500` is a neutral trigger at rest.
const DANGER_CLASS = /^(bg|text)-danger(-\d{2,3})?$/

function hasDangerClass(element: Element): boolean {
  return Array.from(element.classList).some((token) => DANGER_CLASS.test(token))
}

// The form Turbo submits: the element itself, or the form that holds it.
function owningForm(element?: HTMLElement | null): HTMLElement | null {
  if (element?.tagName === "FORM") return element
  return element?.closest?.("form") ?? null
}

function isDestructive(
  element?: HTMLElement | null,
  submitter?: HTMLElement | null,
): boolean {
  const form = owningForm(element)
  if (form?.getAttribute("method")?.toLowerCase() === "delete") return true
  const override = form?.querySelector<HTMLInputElement>(
    'input[name="_method"]',
  )
  if (override?.value.toLowerCase() === "delete") return true
  if (!submitter) return false
  return (
    hasDangerClass(submitter) ||
    Array.from(submitter.querySelectorAll("*")).some(hasDangerClass)
  )
}

function explicitVariant(
  element?: HTMLElement | null,
  submitter?: HTMLElement | null,
): Variant | null {
  const value = [
    element?.dataset?.turboConfirmVariant,
    submitter?.dataset?.turboConfirmVariant,
  ].find(Boolean)
  return value === "danger" || value === "warning" ? value : null
}

// Mirrors conek_ui 0.3.47 Ui::AlertModalComponent / Ui::DeleteModalComponent /
// Ui::ButtonComponent — keep in sync. Icon paths come from the gem's
// app/assets/config/icons.json.
const ICON_PATHS = {
  trash: [
    "M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6",
    "M3 6h18",
    "M8 6V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2",
  ],
  triangle_alert: [
    "m21.73 18-8-14a2 2 0 0 0-3.48 0l-8 14A2 2 0 0 0 4 21h16a2 2 0 0 0 1.73-3",
    "M12 9v4",
    "M12 17h.01",
  ],
  x: ["M18 6 6 18", "m6 6 12 12"],
} as const

function iconSvg(name: keyof typeof ICON_PATHS, className: string): string {
  const paths = ICON_PATHS[name].map((d) => `<path d="${d}"></path>`).join("")
  return `<svg class="${className}" xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">${paths}</svg>`
}

const BUTTON_BASE =
  "overflow-hidden inline-flex items-center rounded-md duration-300 transition-colors cursor-pointer disabled:cursor-not-allowed disabled:opacity-50 font-medium focus-visible:ring-2 focus-visible:ring-offset-2 ring-offset-background focus-visible:outline-none h-10 px-3.5 py-2.5 text-sm grow justify-center text-nowrap sm:grow-0"
// Cancel is outline PRIMARY, not secondary: the React AlertModal (and the
// DeleteModal built on it) renders `<Button intent="outline">` with the
// button's default colour, primary. A grey Cancel made every Hotwire confirm
// differ from its React twin (CONEK-9135).
const BUTTON_VARIANTS = {
  cancel:
    "border focus-visible:ring-primary-500 border-current text-primary-500 hover:text-primary-600 active:border-primary-700 disabled:bg-border disabled:hover:text-primary-500 disabled:active:border-current",
  danger:
    "text-white focus-visible:ring-danger-500 bg-danger-500 hover:bg-danger-600 active:bg-danger-700 disabled:hover:bg-danger-500 disabled:active:bg-danger-500",
  warning:
    "text-white focus-visible:ring-primary-500 bg-primary-500 hover:bg-primary-600 active:bg-primary-700 disabled:hover:bg-primary-500 disabled:active:bg-primary-500",
} as const

export function turboConfirm(
  message: string,
  element?: HTMLElement | null,
  submitter?: HTMLElement | null,
): Promise<boolean> {
  let variant: Variant | null = explicitVariant(element, submitter)
  if (message.startsWith("warning::")) {
    variant ??= "warning"
    message = message.slice(9)
  }
  variant ??= isDestructive(element, submitter) ? "danger" : "warning"

  const title = resolveLabel(element?.dataset?.turboConfirmTitle, "title")
  const cancelLabel = resolveLabel(
    element?.dataset?.turboConfirmCancel,
    "cancel",
  )
  // An <input type="submit|button"> shows its value, not its (empty) text.
  const submitterText =
    submitter instanceof HTMLInputElement
      ? submitter.value.trim()
      : submitter?.textContent?.replace(/\s+/g, " ").trim()
  // Empty values count as unset (see resolveLabel), hence find(Boolean).
  const confirmLabel = resolveLabel(
    [
      element?.dataset?.turboConfirmConfirm,
      submitter?.dataset?.turboConfirmConfirm,
      submitterText,
    ].find(Boolean),
    "confirm",
  )
  // No per-element override for the X: it is dialog chrome, not the action's copy.
  const closeLabel = resolveLabel(undefined, "close")

  const isDanger = variant === "danger"
  const circleBg = isDanger ? "bg-danger/20" : "bg-warning/20"
  const iconColor = isDanger ? "text-danger" : "text-warning"
  const iconName = isDanger ? "trash" : "triangle_alert"

  return new Promise((resolve) => {
    const dialog = document.createElement("dialog")
    dialog.className =
      "relative z-1000 backdrop:bg-overlay/75 backdrop:transition-opacity bg-transparent"
    dialog.dataset.testid = "turbo-confirm-dialog"
    dialog.setAttribute("role", "alertdialog")
    dialog.setAttribute("aria-modal", "true")
    // Accessible name and description from the title and message, as the
    // gem's modals do.
    const idPrefix = `turbo-confirm-${Math.random().toString(36).slice(2, 10)}`
    dialog.setAttribute("aria-labelledby", `${idPrefix}-title`)
    dialog.setAttribute("aria-describedby", `${idPrefix}-description`)

    // Mirrors conek_ui 0.3.47 Ui::AlertModalComponent / Ui::DeleteModalComponent /
    // Ui::ButtonComponent — keep in sync.
    dialog.innerHTML = `
      <div class="fixed inset-0 z-1001 overflow-y-auto h-full w-full">
        <div class="flex min-h-full items-end justify-center sm:items-center">
          <div data-testid="turbo-confirm-panel" class="relative w-full max-w-md transform overflow-hidden whitespace-normal rounded-lg border border-border bg-card px-6 pb-8 pt-6 text-left shadow-xl transition-all my-auto sm:my-8 sm:px-10 max-sm:h-fit max-sm:w-full">
            <div class="absolute right-2 top-2 flex justify-end">
              <button type="button" data-testid="turbo-confirm-close" class="rounded-md bg-card text-foreground-pale hover:text-foreground-medium focus-visible:outline-none cursor-pointer">
                <span class="sr-only" data-close-label></span>
                ${iconSvg("x", "h-5 w-5")}
              </button>
            </div>
            <div class="mb-6 flex flex-col items-center gap-2.5">
              <div data-testid="turbo-confirm-icon" data-icon="${iconName}" class="flex size-10 shrink-0 items-center justify-center rounded-full ${circleBg}">
                ${iconSvg(iconName, `h-5 w-5 ${iconColor}`)}
              </div>
              <h3 id="${idPrefix}-title" class="w-full text-center text-base font-semibold leading-6 text-foreground" data-title></h3>
              <p id="${idPrefix}-description" class="w-full text-center text-sm text-foreground-medium" data-message></p>
            </div>
            <div class="flex flex-wrap justify-center gap-2.5 px-2">
              <button type="button" autofocus data-action="cancel" data-testid="turbo-confirm-cancel" class="${BUTTON_BASE} ${BUTTON_VARIANTS.cancel}">
                <span data-cancel-label></span>
              </button>
              <button type="button" data-action="confirm" data-testid="turbo-confirm-confirm" class="${BUTTON_BASE} gap-2 ${BUTTON_VARIANTS[variant]}">
                <svg data-confirm-spinner class="hidden animate-spin h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" aria-hidden="true">
                  <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                  <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                <span data-confirm-label></span>
              </button>
            </div>
          </div>
        </div>
      </div>
    `

    const setText = (selector: string, text: string) => {
      const node = dialog.querySelector(selector)
      if (node) node.textContent = text
    }

    setText("[data-message]", message)
    setText("[data-title]", title)
    setText("[data-cancel-label]", cancelLabel)
    setText("[data-confirm-label]", confirmLabel)
    setText("[data-close-label]", closeLabel)

    document.body.appendChild(dialog)
    dialog.showModal()

    let resolved = false
    const cleanup = (result: boolean) => {
      if (resolved) return
      resolved = true
      dialog.close()
      dialog.remove()
      resolve(result)
    }

    // Confirm click: don't close the dialog immediately. Flip the button
    // into a loading state (spinner visible, every action disabled) and
    // resolve the promise so Turbo continues with the confirmed request.
    // If the response is a real redirect, Turbo's own navigation unmounts
    // this page (and the dialog with it) — closing here would just flash
    // the old page for a moment while the new one loads. But not every
    // confirmed action navigates: an in-place turbo_stream response, a 422
    // re-render, or a 4xx/5xx error never unmount the page, which used to
    // leave the dialog stuck open forever with both buttons disabled. Close
    // it ourselves once the submission settles, unless a real redirect is
    // under way.
    dialog
      .querySelector("[data-action='confirm']")
      ?.addEventListener("click", () => {
        if (resolved) return
        const confirmBtn = dialog.querySelector<HTMLButtonElement>(
          "[data-action='confirm']",
        )
        const cancelBtn = dialog.querySelector<HTMLButtonElement>(
          "[data-action='cancel']",
        )
        const closeBtn = dialog.querySelector<HTMLButtonElement>(
          "[data-testid='turbo-confirm-close']",
        )
        const spinner = dialog.querySelector("[data-confirm-spinner]")
        if (confirmBtn) confirmBtn.disabled = true
        if (cancelBtn) cancelBtn.disabled = true
        if (closeBtn) closeBtn.disabled = true
        if (spinner) spinner.classList.remove("hidden")
        resolved = true
        resolve(true)

        // Bind the "no-navigation → close ourselves" fallback to THIS
        // dialog's own form. Without the target check any other Turbo
        // submission on the page (e.g. the auto-submit filter bar) that
        // completes first would consume the {once: true} listener and
        // dismiss this dialog while the real destructive request is still
        // in flight.
        const form = owningForm(element)
        const submitEndHandler = (event: Event) => {
          if (form && event.target !== form) return
          document.removeEventListener("turbo:submit-end", submitEndHandler)
          const detail = (event as CustomEvent).detail as
            | {
                success?: boolean
                fetchResponse?: { response?: { redirected?: boolean } }
              }
            | undefined
          const navigating = Boolean(
            detail?.success && detail?.fetchResponse?.response?.redirected,
          )
          if (navigating) return
          dialog.close()
          dialog.remove()
        }
        document.addEventListener("turbo:submit-end", submitEndHandler)
      })
    dialog
      .querySelector("[data-action='cancel']")
      ?.addEventListener("click", () => cleanup(false))
    dialog
      .querySelector("[data-testid='turbo-confirm-close']")
      ?.addEventListener("click", () => cleanup(false))
    dialog.addEventListener("close", () => cleanup(false))
  })
}
